import React, { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "@/layouts/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

// Security note: this page must NEVER read `reset_token` (or any URL /
// link derived from it) from the API response. The plain token is
// delivered exclusively via the email sent by the backend — displaying
// it here would defeat the whole point of the hardening.
const GENERIC_INFO_MESSAGE =
  "Se l'indirizzo è associato a un account, controlla la tua email per reimpostare la password.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // The API always returns the same generic body (whether the email
      // exists or not) — surface a matching generic UI message. Do NOT
      // read any token/link from the response.
      setInfo(GENERIC_INFO_MESSAGE);
    } catch (err) {
      // 429 (rate limit) surfaces via detail; anything else falls back
      // to the same generic UI wording — an attacker MUST NOT be able
      // to tell "email exists" from the error path either.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && detail) {
        setError(formatApiError(detail) || err.message);
      } else {
        setInfo(GENERIC_INFO_MESSAGE);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Recupera password"
      subtitle="Ti inviamo un'email con le istruzioni"
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
        <div>
          <Label htmlFor="email" className="label-uppercase">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 rounded-sm h-11"
            data-testid="forgot-email-input"
          />
        </div>
        {error && (
          <div
            className="text-sm text-destructive border border-destructive/30 bg-destructive/5 p-3 rounded-sm"
            data-testid="forgot-error"
          >
            {error}
          </div>
        )}
        {info && (
          <div
            className="text-sm text-neutral-700 border border-border bg-secondary/60 p-3 rounded-sm"
            data-testid="forgot-info"
          >
            {info}
          </div>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-sm"
          data-testid="forgot-submit-button"
        >
          {busy ? "Invio…" : "Invia richiesta"}
        </Button>
      </form>
      <div className="mt-8 text-sm text-neutral-600">
        <Link to="/login" className="text-primary hover:underline" data-testid="forgot-back-link">
          ← Torna al login
        </Link>
      </div>
    </AuthLayout>
  );
}
